import React, { memo, useMemo } from 'react';
import { useStyles } from 'uinix-ui';

import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import { SharedProgressProps } from '../../types/SharedProgressProps';
import { Box } from '../Box/Box';
import { Layout } from '../Layout/Layout';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';
import { Donut } from './Donut';

interface Props extends SharedProgressProps {
  mode?: 'bar' | 'donut' | 'pill';
}

export const Progress = memo(
  ({
    isLoading,
    max,
    mode = 'bar',
    name,
    status = 'active',
    statsLabel,
    tooltip,
    value,
  }: Props) => {
    const styles = useStyles();
    const formatNumber = useFormatNumber();

    const defaultFormatLabel = (value: number, max: number) =>
      `${formatNumber(value)}/${formatNumber(max)}`;

    const progressStyle = useMemo(
      () => ({
        backgroundColor: 'background.quiet',
        ...(isLoading ? styles.shimmer({}) : {}),
        height: 'progress.height',
        width: '100%',
        '::-moz-progress-bar': {
          backgroundColor: `status.${status}`,
        },
        '::-webkit-progress-bar': {
          backgroundColor: 'transparent',
        },
        '::-webkit-progress-value': {
          backgroundColor: `status.${status}`,
        },
      }),
      [isLoading, status],
    );

    let progress;
    switch (mode) {
      case 'donut': {
        progress = (
          <Donut
            max={max}
            name={name}
            status={status}
            tooltip={tooltip}
            value={value}
          />
        );
        break;
      }
      case 'pill': {
        const percentage = (value / max) * 100;
        progress = (
          <Tooltip tooltip={tooltip} width="100%">
            <Box
              styles={{
                borderRadius: 'pill',
                backgroundColor: 'background.quiet',
                height: 'progress.height',
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                styles={{
                  borderRadius: 'inherit',
                  height: '100%',
                  width: `${percentage}%`,
                  backgroundColor: `status.${status}`,
                  transition: 'width 0.3s ease-in-out',
                }}
              />
            </Box>
          </Tooltip>
        );
        break;
      }
      case 'bar':
      default: {
        progress = (
          <Tooltip tooltip={tooltip} width="100%">
            <Box
              as="progress"
              id={name}
              max={max}
              styles={progressStyle}
              value={value}
            />
          </Tooltip>
        );
        break;
      }
    }

    if (statsLabel) {
      const {
        formatLabel = defaultFormatLabel,
        position = 'left',
        textAlign,
        variant,
        width,
        color,
      } = statsLabel;
      const label = (
        <Text
          aria-labelledby={name}
          flex="none"
          textAlign={textAlign}
          variant={variant}
          w={width}
          color={color}
        >
          {formatLabel(value, max)}
        </Text>
      );

      switch (position) {
        case 'left':
          return (
            <Layout align="center" flex="auto" spacing={2}>
              {label} {progress}
            </Layout>
          );
        case 'top':
          return (
            <Layout direction="column" flex="auto" spacing={2}>
              {label}
              {progress}
            </Layout>
          );
        case 'bottom':
        default:
          return (
            <Layout direction="column" flex="auto" spacing={2}>
              {progress}
              {label}
            </Layout>
          );
      }
    }

    return progress;
  },
);
